import { useEffect, useId, useRef, useState } from "react";
import type { JSX } from "react";
import type { NfcClockResult } from "../../api/nfcSchemas";
import { usePurchasePaths } from "../../platform/PlatformContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import { Alert } from "../../shared/ui/Alert";
import { cx } from "../../shared/ui/classNames";
import { describeErrorCode } from "../../shared/ui/describeError";
import styles from "../../shared/ui/ui.module.css";
import clockStyles from "./ClockPage.module.css";
import { EarlyStartConfirmation } from "./EarlyStartConfirmation";
import type { ClockAction } from "./model/clockActions";
import type { ClockControl } from "./useClockControl";
import type { NfcClockControl, NfcClockOutcome } from "./useNfcClock";

/** Properties of {@link NfcClock}. */
export interface NfcClockProps {
  /** Clocking at a tag. */
  readonly control: NfcClockControl;
  /** The steps of the current state. */
  readonly actions: readonly ClockAction[];
  /** Label of a step. */
  readonly actionLabel: (action: ClockAction) => string;
  /** IANA time zone of the employment. */
  readonly timeZone: string;
  /** End of a running rest period (formatted); a clock-in asks for confirmation first, like the normal step. */
  readonly earlyStartRestEnd: string | null;
  /** Whether the page records another step right now. */
  readonly busy: boolean;
  /**
   * Whether own steps of this device still wait to be sent: the state at the tag would not fit the server's, so the
   * steps at the tag wait until they are sent (F8 of the review of T-174).
   */
  readonly pending: boolean;
  /** Called after the server accepted the step. */
  readonly onRecorded: (action: ClockAction, result: NfcClockResult) => void;
  /**
   * Whether the section sits inside another titled container (the panel of the compact clock):
   * then it has a third-level heading and no card of its own.
   */
  readonly nested?: boolean | undefined;
}

/**
 * Returns the key of a step among the steps at the tag.
 *
 * @param action - The step.
 * @returns The key.
 */
function stepKey(action: ClockAction): string {
  const key = `${action.kind}-${action.breakQualification ?? ""}`;

  return key;
}

/**
 * Steps at a workplace NFC tag (variant A, T-174): the person chooses the step and then holds the phone to the tag
 * (`SINV-01`: the step is the person's action, the tag only proves the place). The server checks the tag; an unknown,
 * blocked or forged tag records nothing and the reason is shown. No location is captured at a tag (ADR-0144).
 *
 * @param props - Component properties.
 * @returns The section.
 */
export function NfcClock(props: NfcClockProps): JSX.Element {
  const { t } = useI18n();
  const purchasePaths = usePurchasePaths();
  const headingId = useId();
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmClockIn, setConfirmClockIn] = useState<ClockAction | null>(null);
  const { cancel } = props.control;
  // A reading still running when the section goes away (another page, the panel closed) is ended,
  // so a tag touched later records nothing out of sight (F-78).
  const readingRef = useRef(false);
  const cancelRef = useRef(cancel);
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const actionButtons = useRef(new Map<string, HTMLButtonElement>());
  // The step whose button holds the focus after a reading; if the button goes away (the steps of
  // the new state), the focus moves to the heading instead of the page (F-87, ADR-0211).
  const focusedStepRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    cancelRef.current = cancel;
  });

  useEffect(() => {
    const step = focusedStepRef.current;

    if (step === null) {
      return;
    }

    const active = document.activeElement;

    if (step.isConnected) {
      // The person moved the focus on: nothing to keep.
      if (active !== step) {
        focusedStepRef.current = null;
      }

      return;
    }

    focusedStepRef.current = null;

    if (active === null || active === document.body) {
      headingRef.current?.focus();
    }
  });

  useEffect(() => {
    const cancelOnLeave = (): void => {
      if (readingRef.current) {
        cancelRef.current?.();
      }
    };

    return cancelOnLeave;
  }, []);

  const describe = (outcome: Exclude<NfcClockOutcome, { kind: "recorded" }>): string => {
    const message =
      outcome.kind === "notRead"
        ? t("nfc.clock.notRead")
        : outcome.kind === "disabled"
          ? t("nfc.clock.disabled")
          : outcome.code === null
            ? t("nfc.clock.failed")
            : describeErrorCode(outcome.code, t, purchasePaths);

    return message;
  };

  const run = async (action: ClockAction): Promise<void> => {
    const button = actionButtons.current.get(stepKey(action)) ?? null;
    setConfirmClockIn(null);
    setError(null);
    setReading(true);
    readingRef.current = true;
    // The step keeps the focus while the tag is read (`aria-disabled`), also when it was confirmed in
    // the question about the early start, which closes now: Escape still reaches the panel of the
    // compact clock, and the request to hold the phone to the tag is announced undisturbed.
    button?.focus();
    const outcome = await props.control.clock({
      action,
      timeZoneId: props.timeZone,
      prompt: t("nfc.clock.prompt", { action: props.actionLabel(action) }),
    });
    readingRef.current = false;
    const active = document.activeElement;

    // „Abbrechen“ goes away with the reading: its focus returns to the step.
    if (active === null || active === document.body || sectionRef.current?.contains(active)) {
      button?.focus();
      focusedStepRef.current = button;
    }

    setReading(false);

    if (outcome.kind === "recorded") {
      props.onRecorded(action, outcome.result);
    } else {
      setError(describe(outcome));
    }
  };

  const start = (action: ClockAction): void => {
    if (action.kind === "ClockIn" && props.earlyStartRestEnd !== null) {
      setConfirmClockIn(action);
      return;
    }

    void run(action);
  };

  const nested = props.nested === true;
  const heading = t("nfc.clock.title");
  const section = (
    <section
      ref={sectionRef}
      className={cx(nested ? undefined : styles["card"], styles["stack"])}
      aria-labelledby={headingId}
    >
      {nested ? (
        <h3 ref={headingRef} id={headingId} tabIndex={-1}>
          {heading}
        </h3>
      ) : (
        <h2 ref={headingRef} id={headingId} tabIndex={-1}>
          {heading}
        </h2>
      )}
      <p className={styles["muted"]}>{t("nfc.clock.hint")}</p>
      <div className={styles["buttonGrid"]}>
        {props.actions.map((action) => (
          <button
            key={stepKey(action)}
            ref={(element) => {
              if (element === null) {
                actionButtons.current.delete(stepKey(action));
              } else {
                actionButtons.current.set(stepKey(action), element);
              }
            }}
            type="button"
            className={styles["button"]}
            disabled={props.busy || props.pending}
            // While a tag is read the steps wait but stay focusable, so the focus never drops to the
            // page (F-87, ADR-0211).
            aria-disabled={reading ? true : undefined}
            onClick={() => {
              if (!reading) {
                start(action);
              }
            }}
          >
            {t("nfc.clock.action", { action: props.actionLabel(action) })}
          </button>
        ))}
      </div>
      {props.pending ? (
        <p className={cx(styles["muted"], clockStyles["footnote"])}>{t("nfc.clock.pending")}</p>
      ) : null}
      {/* Always rendered, so screen readers announce the request to hold the phone to the tag (F6). */}
      <p role="status" className={cx(styles["muted"], clockStyles["footnote"])}>
        {reading ? t("nfc.clock.reading") : ""}
      </p>
      {reading && cancel !== null ? (
        <div>
          <button type="button" className={styles["button"]} onClick={cancel}>
            {t("common.cancel")}
          </button>
        </div>
      ) : null}
      {confirmClockIn === null || props.earlyStartRestEnd === null ? null : (
        <EarlyStartConfirmation
          restEnd={props.earlyStartRestEnd}
          onConfirm={() => {
            void run(confirmClockIn);
          }}
          onCancel={() => {
            setConfirmClockIn(null);
          }}
        />
      )}
      {error === null ? null : <Alert tone="danger">{error}</Alert>}
    </section>
  );

  return section;
}

/** Properties of {@link ClockNfc}. */
export interface ClockNfcProps {
  /** The clock of the person. */
  readonly control: ClockControl;
  /** Whether the section sits inside the panel of the compact clock. */
  readonly nested?: boolean | undefined;
}

/**
 * The steps at a workplace NFC tag of the shared clock (F-78): the start page and the compact clock
 * show the same section with the same rules (steps of the state, early start asked first, steps
 * wait while own entries are not sent) from {@link useClockControl}. Hidden where NFC is not
 * available.
 *
 * @param props - Component properties.
 * @returns The section, or `null`.
 */
export function ClockNfc(props: ClockNfcProps): JSX.Element | null {
  const { control } = props;
  const section = control.nfc.available ? (
    <NfcClock
      control={control.nfc}
      actions={control.actions}
      actionLabel={control.actionLabel}
      timeZone={control.timeZone}
      earlyStartRestEnd={
        control.restActive && control.earliestStart !== null
          ? control.timeText(control.earliestStart)
          : null
      }
      busy={control.busy}
      pending={control.pendingOwn}
      onRecorded={control.nfcRecorded}
      nested={props.nested}
    />
  ) : null;

  return section;
}
